import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import type { Account } from '@syf-api-library/credit-accounts'
import { CARECREDIT } from 'const/account/clients'
import { DEEP_LINK_FEATURES } from 'const/deepLink'
import { customCardArtModalContainerId } from 'const/modalContainerId'
import routes from 'const/routes'
import deepLinkStorage from 'helpers/deepLinkStorage'
import { setDeepLinkStatus } from 'helpers/deepLinkAnalytics'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import getQueryParam from 'syf-js-utilities/formats/getQueryParam'

const useCustomCardDeepLink = (
  accounts: Account[],
  isAccountsFetched: boolean,
  isCreditAccountsAPIError: boolean,
  isTokenFetched: boolean,
  electronicConsent: boolean
): void => {
  const navigate = useNavigate()
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const updateModalStatus = useSetAtom(modalStatusAtom)
  const hasRun = useRef(false)
  const feature =
    deepLinkStorage.getFeature() ||
    getQueryParam('feature') ||
    getQueryParam('link')

  useEffect(() => {
    if (
      hasRun.current ||
      feature !== DEEP_LINK_FEATURES.customcard ||
      !isTokenFetched ||
      !isAccountsFetched ||
      !electronicConsent
    )
      return

    hasRun.current = true
    deepLinkStorage.clearAll()

    const careCreditAccounts = accounts.filter(
      (account) => account.product?.portfolio?.toLowerCase() === CARECREDIT
    )
    const accountId = careCreditAccounts[0]?.info['cipher.accountId']

    if (
      isCreditAccountsAPIError ||
      careCreditAccounts.length !== 1 ||
      careCreditAccounts[0]?.registration?.registered !== true ||
      !accountId
    ) {
      setDeepLinkStatus('failure')
      navigate(routes.HOME)
      return
    }

    setSelectedAccount(accountId)
    updateModalStatus('CustomCardArt', customCardArtModalContainerId(accountId))
    setDeepLinkStatus('success')
  }, [
    accounts,
    electronicConsent,
    feature,
    isAccountsFetched,
    isCreditAccountsAPIError,
    isTokenFetched,
    navigate,
    setSelectedAccount,
    updateModalStatus
  ])
}

export default useCustomCardDeepLink
